import React from "react";
import { useLocation } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";
import { SEO, SCHEMA_ORGANIZATION } from "@/hooks/useSEO";
import { OpeningSeasonPopup } from "@/components/OpeningSeasonPopup";

// ── Blocs des 8 univers ──────────────────────────────────────────────────────
// Ordre UNIQUE (desktop + mobile) : VISITER, DORMIR, MARIAGE, GROUPE, OFFRIR, GÎTES, ÉVÉNEMENTS, ROSERAIE
const BLOCKS = [
  {
    id: "visiter",
    labelFr: "VISITE",    labelEn: "VISIT",       labelDe: "BESICHTIGEN", labelNl: "BEZOEKEN",
    subFr: "Visite du Château",
    subEn: "Visit the Château",
    subDe: "Besichtigung des Schlosses",
    subNl: "Bezoek aan het Château",
    img: "/media/HOME_VISITER_254fdf16_opt_5547b557.webp",
    href: "/visiter",
    position: "center 40%",
  },
  {
    id: "dormir",
    labelFr: "CHAMBRES",  labelEn: "SLEEP",        labelDe: "SCHLAFEN",   labelNl: "SLAPEN",
    subFr: "Chambres d'hôtes au château",
    subEn: "Bed & breakfast at the castle",
    subDe: "Gästezimmer im Schloss",
    subNl: "B&B in het kasteel",
    img: "/media/HOME_DORMIR_dc26c9c6_opt_b14e543a.webp",
    href: "/dormir",
    position: "center 30%",
  },
  {
    id: "mariage",
    labelFr: "MARIAGE",   labelEn: "WEDDING",      labelDe: "HOCHZEIT",   labelNl: "BRUILOFT",
    subFr: "Mariages & réceptions d'exception",
    subEn: "Exceptional weddings & receptions",
    subDe: "Außergewöhnliche Hochzeiten",
    subNl: "Uitzonderlijke bruiloften",
    img: "/media/HeaderMariage_db8a2cf7_opt_deaa0eb4.webp",
    href: "/mariage",
    position: "center 50%",
  },
  {
    id: "groupe",
    labelFr: "GROUPE",    labelEn: "GROUPS",       labelDe: "GRUPPEN",    labelNl: "GROEPEN",
    subFr: "Visites privées & séjours en groupe",
    subEn: "Private tours & group stays",
    subDe: "Privatführungen & Gruppenaufenthalte",
    subNl: "Privérondleidingen & groepsverblijven",
    img: "/media/HOME_GROUPE_379a7c62_opt_6de7f0c2.webp",
    href: "/groupe",
    position: "center 40%",
  },
  {
    id: "offrir",
    labelFr: "OFFRIR",    labelEn: "GIFT",         labelDe: "SCHENKEN",   labelNl: "CADEAU",
    subFr: "Bons cadeaux & séjours offerts",
    subEn: "Gift vouchers & gifted stays",
    subDe: "Geschenkgutscheine & Aufenthalte",
    subNl: "Cadeaubonnen & geschonken verblijven",
    img: "/media/HELENA_8f19076c_af60f09c.webp",
    href: "/offrir",
    position: "center center",
  },
  {
    id: "gites",
    labelFr: "GÎTES",     labelEn: "GÎTES",        labelDe: "FERIENHÄUSER", labelNl: "VAKANTIEHUIZEN",
    subFr: "Gîtes César & Rosalie",
    subEn: "Gîtes César & Rosalie",
    subDe: "Ferienhäuser César & Rosalie",
    subNl: "Vakantiehuizen César & Rosalie",
    img: "/media/jack_gites_6f72e566_opt_98475e5e.webp",
    href: "/gites",
    position: "center 40%",
  },
  {
    id: "evenement",
    labelFr: "ÉVÉNEMENTS", labelEn: "EVENTS",      labelDe: "VERANSTALTUNGEN", labelNl: "EVENEMENTEN",
    subFr: "Séminaires, galas & réceptions d'entreprise",
    subEn: "Seminars, galas & corporate events",
    subDe: "Seminare, Galas & Firmenveranstaltungen",
    subNl: "Seminars, galas & bedrijfsevenementen",
    img: "/media/HOME_EVENEMENT_dd00732c_opt_e7c242a8.webp",
    href: "/evenement",
    position: "center 60%",
  },
  {
    id: "roseraie",
    labelFr: "ROSERAIE",  labelEn: "ROSE GARDEN",  labelDe: "ROSENGARTEN", labelNl: "ROZENTUIN",
    subFr: "Jardin de roses d'exception",
    subEn: "Exceptional rose garden",
    subDe: "Außergewöhnlicher Rosengarten",
    subNl: "Uitzonderlijke rozentuin",
    img: "/media/home-roseraie-2026-10-15_f9cbfafc.jpg",
    href: "/roseraie",
    position: "center 40%",
  },
];

const HOME_VISITER_SRC_SET = "/media/home-visiter-480_ca74f840.webp 480w, /media/home-visiter-768_3eec0f41.webp 768w, /media/home-visiter-1280_55d4dce2.webp 1280w";
const HOME_VISITER_SIZES = "(max-width: 640px) 100vw, 25vw";
const HOME_VISITER_FALLBACK = "/media/home-visiter-1280_55d4dce2.webp";

// ── Logo ─────────────────────────────────────────────────────────────────────
function LogoVP({ size = 140 }: { size?: number }) {
  return (
    <div style={{
      width: size, height: size,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.94)",
      backdropFilter: "blur(16px)",
      border: "1.5px solid rgba(184,151,90,0.35)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 8px 40px rgba(0,0,0,0.45), 0 0 0 6px rgba(184,151,90,0.08)",
      overflow: "hidden", padding: "12px",
    }}>
      <img
        src="/media/villa-palladienne-logo_a4541543.webp"
        alt="La Villa Palladienne — Château de Syam"
        style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center center", display: "block", transform: "translateY(-2%)" }}
      />
    </div>
  );
}

// ── Mise en forme de l'accueil ───────────────────────────────────────────────
// En CSS (et non selon la largeur lue en JavaScript) : le HTML pré-rendu s'affiche
// tout de suite avec la bonne mise en page, sur mobile comme sur ordinateur,
// avant le chargement du JavaScript, et React reprend la page sans rien déplacer.
const ACCUEIL_CSS = `
  .accueil-page { width: 100vw; background: #0e0c0a; overflow-x: hidden; }
  .accueil-grille {
    display: grid; grid-template-columns: repeat(4, 1fr); grid-template-rows: 50vh 50vh;
    width: 100vw; height: 100vh; position: relative; overflow: hidden;
  }
  .accueil-cadre { height: 100%; overflow: hidden; animation: accueil-entree 0.7s ease both; animation-delay: var(--delai, 0ms); }
  @keyframes accueil-entree { from { transform: scale(1.02); } to { transform: scale(1); } }
  @keyframes accueil-entree-mobile { from { transform: translateY(20px); } to { transform: translateY(0); } }
  .accueil-tuile { position: relative; overflow: hidden; display: block; width: 100%; height: 100%; cursor: pointer; color: #fff; text-decoration: none; }
  .accueil-tuile:focus-visible { outline: 2px solid #b8975a; outline-offset: -4px; }
  .accueil-tuile-image {
    position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
    transform: scale(1); transition: transform 0.7s cubic-bezier(0.23, 1, 0.32, 1); will-change: transform;
  }
  .accueil-tuile-contenu {
    position: relative; z-index: 2; display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 100%; padding: 3rem 1.5rem; text-align: center; gap: 0.6rem;
  }
  .accueil-etiquette {
    display: inline-block; background: rgba(14,12,10,0.68); border-radius: 2px; padding: 0.55rem 1.4rem;
    border: 1px solid rgba(255,255,255,0.12); transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }
  .accueil-etiquette-texte {
    display: block; font-family: 'DM Serif Display', serif; font-size: clamp(2rem, 3.2vw, 3.2rem); font-weight: 400;
    letter-spacing: 0.1em; color: #fff; text-transform: uppercase; line-height: 1; text-shadow: 0 2px 12px rgba(0,0,0,0.5); text-align: center;
  }
  .accueil-tuile--long .accueil-etiquette-texte { font-size: clamp(1.5rem, 2.4vw, 2.5rem); font-weight: 300; letter-spacing: 0.08em; }
  .accueil-sous-titre {
    display: flex; align-items: center; justify-content: center; width: 100%; min-height: 2.7em;
    font-family: 'Poppins', sans-serif; font-size: 0.82rem; line-height: 1.35; letter-spacing: 0.18em; text-transform: uppercase;
    color: rgba(255,255,255,0.82); text-shadow: 0 1px 6px rgba(0,0,0,0.7);
    opacity: 0; transform: translateY(6px); transition: opacity 0.35s ease, transform 0.35s ease;
  }
  .accueil-fleche {
    width: 28px; height: 28px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.5);
    display: flex; align-items: center; justify-content: center;
    opacity: 0; transform: translateY(8px) scale(0.8); transition: opacity 0.3s ease 0.05s, transform 0.3s ease 0.05s;
  }
  .accueil-tuile:hover .accueil-tuile-image, .accueil-tuile:focus-visible .accueil-tuile-image { transform: scale(1.06); }
  .accueil-tuile:hover .accueil-etiquette, .accueil-tuile:focus-visible .accueil-etiquette { transform: translateY(-4px); }
  .accueil-tuile:hover .accueil-sous-titre, .accueil-tuile:focus-visible .accueil-sous-titre { opacity: 1; transform: translateY(0); }
  .accueil-tuile:hover .accueil-fleche, .accueil-tuile:focus-visible .accueil-fleche { opacity: 1; transform: translateY(0) scale(1); }

  .accueil-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between; padding: 0.85rem 2.5rem;
    background: linear-gradient(to bottom, rgba(14,12,10,0.7) 0%, transparent 100%); pointer-events: auto;
  }
  .accueil-nav-logo { width: 120px; height: 120px; object-fit: contain; filter: invert(1) brightness(2); flex-shrink: 0; }
  .accueil-nav-tel {
    display: flex; align-items: center; gap: 0.5rem; background: rgba(184,151,90,0.15); border: 1px solid rgba(184,151,90,0.45);
    color: rgba(255,255,255,0.9); text-decoration: none; font-family: 'Poppins', sans-serif; font-size: 0.78rem;
    letter-spacing: 0.15em; text-transform: uppercase; padding: 0.6rem 1.4rem; transition: all 0.25s ease;
  }
  .accueil-nav-tel:hover { background: rgba(184,151,90,0.3); border-color: rgba(184,151,90,0.8); }
  .accueil-nav-texte { display: flex; flex-direction: column; gap: 1px; }
  .accueil-mobile { display: none; }
  .accueil-pied { padding: 2.5rem clamp(1.5rem,4vw,4rem); }

  @media (max-width: 767px) {
    .accueil-grille { display: flex; flex-direction: column; height: auto; overflow: visible; }
    .accueil-cadre { height: auto; overflow: visible; animation-name: accueil-entree-mobile; animation-duration: 0.5s; }
    .accueil-tuile { height: 52vw; min-height: 180px; }
    .accueil-tuile-contenu { padding: 2.5rem 1rem; }
    .accueil-etiquette { padding: 0.45rem 1.1rem; }
    .accueil-etiquette-texte { font-size: clamp(1.6rem, 7vw, 2.4rem); }
    .accueil-tuile--long .accueil-etiquette-texte { font-size: clamp(1.3rem, 5.5vw, 2rem); }
    .accueil-sous-titre { min-height: 0; font-size: 0.75rem; opacity: 1; transform: none; }
    .accueil-fleche { opacity: 0.7; transform: none; }
    .accueil-nav { padding: 0.75rem 1.25rem; }
    .accueil-nav-logo { width: 68px; height: 68px; }
    .accueil-nav-tel { font-size: 0.72rem; padding: 0.5rem 0.9rem; }
    .accueil-ordi { display: none; }
    .accueil-mobile { display: inline; }
    .accueil-pied { padding: 2rem 1.5rem; }
  }
  @media (prefers-reduced-motion: reduce) {
    .accueil-cadre { animation: none; }
    .accueil-tuile-image, .accueil-etiquette, .accueil-sous-titre, .accueil-fleche { transition: none; }
  }
`;

// ── Bloc individuel ──────────────────────────────────────────────────────────
// Un vrai lien (<a href>) : suivi par les moteurs de recherche, utilisable au clavier,
// ouvrable dans un nouvel onglet ; un clic simple navigue sans recharger la page.
function Block({
  block, lang, index, onNavigate,
}: {
  block: typeof BLOCKS[0]; lang: string; index: number; onNavigate: (href: string) => void;
}) {
  const label =
    lang === "en" ? block.labelEn :
    lang === "de" ? block.labelDe :
    lang === "nl" ? block.labelNl :
    block.labelFr;

  const sub =
    lang === "en" ? block.subEn :
    lang === "de" ? block.subDe :
    lang === "nl" ? block.subNl :
    block.subFr;

  // Les 4 premières tuiles sont visibles dès l'arrivée (rangée du haut sur ordinateur,
  // haut de la colonne sur mobile) ; les suivantes sont chargées à l'approche (natif, sans JavaScript).
  const priority = index < 4;

  return (
    <a
      href={block.href}
      className={block.id === "evenement" ? "accueil-tuile accueil-tuile--long" : "accueil-tuile"}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onNavigate(block.href);
      }}
    >
      <img
        src={block.id === "visiter" ? HOME_VISITER_FALLBACK : block.img}
        srcSet={block.id === "visiter" ? HOME_VISITER_SRC_SET : undefined}
        sizes={block.id === "visiter" ? HOME_VISITER_SIZES : undefined}
        alt=""
        aria-hidden="true"
        className="accueil-tuile-image"
        loading={priority ? "eager" : "lazy"}
        fetchPriority={index < 2 ? "high" : "auto"}
        decoding="async"
        style={{ objectPosition: block.position }}
      />

      <span className="accueil-tuile-contenu">
        <span className="accueil-etiquette">
          <span className="accueil-etiquette-texte">{label}</span>
        </span>
        <span className="accueil-sous-titre">{sub}</span>
        <span className="accueil-fleche" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2l4 4-4 4M2 6h8" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </span>
    </a>
  );
}

// ── Navbar ───────────────────────────────────────────────────────────────────
function HomeNavbar() {
  return (
    <div className="accueil-nav">
      {/* Logo */}
      <a href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none", gap: "0.75rem" }}>
        <img
          src="/media/villa-palladienne-logo-cropped_3afff7ae.webp"
          alt="La Villa Palladienne — Château de Syam"
          width={120}
          height={120}
          className="accueil-nav-logo"
        />
        <span className="accueil-ordi accueil-nav-texte">
          <span style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "1rem", fontWeight: 400,
            color: "rgba(255,255,255,0.95)",
            letterSpacing: "0.08em",
            lineHeight: 1.2,
            textShadow: "0 1px 6px rgba(0,0,0,0.5)",
          }}>La Villa Palladienne</span>
          <span style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "0.65rem", letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.85)",
            lineHeight: 1,
          }}>Château de Syam</span>
        </span>
      </a>
      {/* Bouton Contact (appel téléphonique) */}
      <a href="tel:0384516414" className="accueil-nav-tel">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.06 6.06l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
        <span className="accueil-mobile">Contact</span>
        <span className="accueil-ordi">03 84 51 64 14</span>
      </a>
    </div>
  );
}

// ── Footer page accueil ───────────────────────────────────────────────────────
function HomeFooter() {
  return (
    <footer className="accueil-pied" style={{
      background: "#0a0a0a",
      color: "rgba(255,255,255,0.4)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "1.25rem",
      fontSize: "0.82rem",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      borderTop: "1px solid rgba(255,255,255,0.07)",
    }}>
      <img
        src="/media/villa-palladienne-logo-cropped_3afff7ae.webp"
        alt="La Villa Palladienne — Château de Syam"
        width={52}
        height={52}
        style={{ height: "52px", width: "52px", objectFit: "contain", filter: "invert(1) brightness(0.6)", opacity: 0.7 }}
      />
      <p style={{ margin: 0, textAlign: "center", lineHeight: 1.8, fontSize: "0.82rem", letterSpacing: "0.1em", color: "rgba(255,255,255,0.35)" }}>
        225 Chemin de Benaisy — 39300 Syam, Jura<br />
        <a href="tel:0384516414" style={{ color: "rgba(255,255,255,0.35)", textDecoration: "none" }}>03 84 51 64 14</a>
      </p>
      <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap", justifyContent: "center" }}>
        {[
          { label: "Mentions légales", href: "/legal" },
          { label: "Confidentialité", href: "/privacy" },
          { label: "CGV", href: "/cgv" },
        ].map(link => (
          <a key={link.href} href={link.href} style={{ color: "rgba(255,255,255,0.3)", textDecoration: "none", fontFamily: "'Poppins', sans-serif", fontSize: "0.65rem", letterSpacing: "0.12em" }}>
            {link.label}
          </a>
        ))}
      </div>
      <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
        <a href="https://www.facebook.com/chateaudesyam/" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
          style={{ width: "2.2rem", height: "2.2rem", border: "1px solid rgba(184,151,90,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.3)", textDecoration: "none" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
        </a>
        <a href="https://www.instagram.com/la_villa_palladienne_syam/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
          style={{ width: "2.2rem", height: "2.2rem", border: "1px solid rgba(184,151,90,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.3)", textDecoration: "none" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
        </a>
      </div>
      <p style={{ margin: 0, fontSize: "0.6rem", color: "rgba(255,255,255,0.2)", letterSpacing: "0.1em" }}>
        © {new Date().getFullYear()} La Villa Palladienne — Château de Syam
      </p>
      <a href="https://llp-agency.com/" target="_blank" rel="noopener noreferrer"
        style={{ color: "rgba(255,255,255,0.18)", textDecoration: "none", fontSize: "0.6rem", letterSpacing: "0.1em" }}>
        Créé, propulsé, réalisé avec LLP Agency
      </a>
    </footer>
  );
}

// ── Page principale ──────────────────────────────────────────────────────────
export default function NewHome() {
  const [location, navigate] = useLocation();
  const { lang: language, t } = useLanguage();
  // Accueils /en, /de, /nl : chaque version a sa propre adresse canonique (hreflang).
  const seoPath = location === "/en" || location === "/de" || location === "/nl" ? location : "/";

  const handleNav = (href: string) => {
    window.scrollTo({ top: 0, behavior: "instant" });
    navigate(href);
  };

  return (
    <div className="accueil-page">
      <style>{ACCUEIL_CSS}</style>
      <SEO
        title={t("home_seo_title")}
        description={t("home_seo_description")}
        keywords="Château de Syam, villa néo-palladienne, monument historique Jura, visites guidées château, chambres d'hôtes Jura, mariage château Jura"
        path={seoPath}
        image={HOME_VISITER_FALLBACK}
        heroSrcSet={HOME_VISITER_SRC_SET}
        heroSizes={HOME_VISITER_SIZES}
        jsonLd={SCHEMA_ORGANIZATION}
        hreflang="home"
      />
      {/* H1 et H2 visually-hidden pour le SEO */}
      <h1 style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>Château de Syam — Villa Palladienne, Jura</h1>
      <h2 style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>{t("home_h2")}</h2>

      {/* Navbar fixe */}
      <HomeNavbar />
      <OpeningSeasonPopup />

      {/* ── Grille des 8 univers : 4×2 plein écran sur ordinateur, une colonne sur mobile ── */}
      <nav className="accueil-grille" aria-label="Château de Syam">
        {BLOCKS.map((block, i) => (
          <div key={block.id} className="accueil-cadre" style={{ "--delai": `${i * 70}ms` } as React.CSSProperties}>
            <Block block={block} lang={language} index={i} onNavigate={handleNav} />
          </div>
        ))}
      </nav>

      {/* ── Footer (visible au scroll) ── */}
      <HomeFooter />
    </div>
  );
}
